import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  createBlankJournalEntry,
  createJournalEntryFromAnalysis,
  deleteJournalEntry,
  fetchJournalEntries,
  fetchRecentAnalyses,
  updateJournalEntry,
} from '../lib/journal';
import { trackEvent } from '../lib/analytics';
import AppBrand from '../components/AppBrand';
import { trackProductEvent } from '../lib/productEvents';

const JOURNAL_ENTRIES_PER_PAGE = 8;

function JournalPage({ session, theme = 'dark', setTheme }) {
  const navigate = useNavigate();
  const location = useLocation();

  const darkMode = theme === 'dark';

  const handleThemeToggle = () => {
    if (!setTheme) return;
    setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'));
  };

  const [entries, setEntries] = useState([]);
  const [recentAnalyses, setRecentAnalyses] = useState([]);
  const [selectedEntryId, setSelectedEntryId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [creatingFromAnalysisId, setCreatingFromAnalysisId] = useState('');
  const [error, setError] = useState('');
  const journalListTopRef = useRef(null);

  const selectedEntry = useMemo(
    () => entries.find((entry) => entry.id === selectedEntryId) || null,
    [entries, selectedEntryId]
  );

  const totalPages = Math.max(
    1,
    Math.ceil(entries.length / JOURNAL_ENTRIES_PER_PAGE)
  );

  const paginatedEntries = useMemo(() => {
    const startIndex = (currentPage - 1) * JOURNAL_ENTRIES_PER_PAGE;
    const endIndex = startIndex + JOURNAL_ENTRIES_PER_PAGE;
    return entries.slice(startIndex, endIndex);
  }, [entries, currentPage]);

  useEffect(() => {
  trackEvent('journal_opened');

  void trackProductEvent({
    userId: session.user.id,
    eventName: 'journal_opened',
  });
}, [session.user.id]);

  useEffect(() => {
    let mounted = true;

    async function loadPage() {
      try {
        setLoading(true);
        setError('');

        const [journalRows, analysisRows] = await Promise.all([
          fetchJournalEntries(session.user.id),
          fetchRecentAnalyses(session.user.id),
        ]);

        if (!mounted) return;

        setEntries(journalRows);
        setRecentAnalyses(analysisRows);

        if (location.state?.seedAnalysis) {
          const existing = journalRows.find(
            (entry) => entry.analysis_id === location.state.seedAnalysis.id
          );

          if (existing) {
            const existingIndex = journalRows.findIndex(
              (entry) => entry.id === existing.id
            );
            const existingPage =
              Math.floor(existingIndex / JOURNAL_ENTRIES_PER_PAGE) + 1;

            setCurrentPage(existingPage);
            setSelectedEntryId(existing.id);
          } else {
            const created = await createJournalEntryFromAnalysis({
              userId: session.user.id,
              analysis: location.state.seedAnalysis,
              source: 'analysis_seed',
            });

            if (!mounted) return;

            const nextEntries = [created, ...journalRows];
            setEntries(nextEntries);
            setCurrentPage(1);
            setSelectedEntryId(created.id);

            trackEvent('journal_entry_created', {
              source: 'analysis_seed',
              ticker: created.ticker || '',
            });
          }

          window.history.replaceState({}, document.title);
          return;
        }

        setSelectedEntryId(null);
        setCurrentPage(1);
      } catch (err) {
        console.error(err);
        if (mounted) setError(err.message || 'Failed to load journal.');
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadPage();

    return () => {
      mounted = false;
    };
  }, [location.state, session.user.id]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  async function handleCreateBlank() {
    try {
      setSaving(true);
      setError('');

      const created = await createBlankJournalEntry({ userId: session.user.id });

      setEntries((prev) => [created, ...prev]);
      setCurrentPage(1);
      setSelectedEntryId(created.id);

      trackEvent('journal_entry_created', {
        source: 'blank',
        ticker: created.ticker || '',
      });
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to create journal entry.');
    } finally {
      setSaving(false);
    }
  }

  async function handleCreateFromAnalysis(analysis) {
    try {
      setCreatingFromAnalysisId(analysis.id);
      setError('');

      const existing = entries.find((entry) => entry.analysis_id === analysis.id);

      if (existing) {
        const existingIndex = entries.findIndex((entry) => entry.id === existing.id);
        const existingPage =
          Math.floor(existingIndex / JOURNAL_ENTRIES_PER_PAGE) + 1;

        setCurrentPage(existingPage);
        setSelectedEntryId(existing.id);
        return;
      }

      const created = await createJournalEntryFromAnalysis({
        userId: session.user.id,
        analysis,
        source: 'journal_quick_add',
      });

      setEntries((prev) => [created, ...prev]);
      setCurrentPage(1);
      setSelectedEntryId(created.id);

      trackEvent('journal_entry_created', {
        source: 'analysis',
        ticker: analysis.ticker || '',
      });
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to create journal entry from analysis.');
    } finally {
      setCreatingFromAnalysisId('');
    }
  }

  async function handleFieldChange(field, value) {
    if (!selectedEntry) return;

    const nextEntries = entries.map((entry) =>
      entry.id === selectedEntry.id ? { ...entry, [field]: value } : entry
    );
    setEntries(nextEntries);

    try {
      await updateJournalEntry(selectedEntry.id, { [field]: value });

      if (field === 'outcome_status') {
        trackEvent('journal_outcome_updated', {
          outcome_status: value,
        });
      }

      if (field === 'followed_plan') {
        trackEvent('journal_followed_plan_updated', {
          followed_plan: value,
        });
      }

      const safeMetadata = {
        field,
      };

      if (
        field === 'outcome_status' ||
        field === 'followed_plan' ||
        field === 'research_intent'
      ) {
        safeMetadata.value = value;
      }

      void trackProductEvent({
        userId: session.user.id,
        eventName: 'journal_field_updated',
        journalEntryId: selectedEntry.id,
        ticker: selectedEntry.ticker,
        metadata: safeMetadata,
      });
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to save changes.');
    }
  }

  async function handleDeleteEntry(entryId) {
    try {
      setError('');
      await deleteJournalEntry(entryId);

      const remaining = entries.filter((entry) => entry.id !== entryId);
      setEntries(remaining);

      if (selectedEntryId === entryId) {
        setSelectedEntryId(null);
      }

      const nextTotalPages = Math.max(
        1,
        Math.ceil(remaining.length / JOURNAL_ENTRIES_PER_PAGE)
      );

      if (currentPage > nextTotalPages) {
        setCurrentPage(nextTotalPages);
      }
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to delete journal entry.');
    }
  }

  const toggleEntryOpen = (entryId) => {
    const willOpen = selectedEntryId !== entryId;

    setSelectedEntryId((previous) =>
      previous === entryId ? null : entryId
    );

    if (willOpen) {
      const entry = entries.find(
        (item) => item.id === entryId
      );

      if (entry) {
        void trackProductEvent({
          userId: session.user.id,
          eventName: 'journal_reopened',
          journalEntryId: entry.id,
          analysisId: entry.analysis_id || null,
          ticker: entry.ticker,
        });
      }
    }
  };

  const formatOutcomeLabel = (outcome) => {
    if (!outcome || outcome === 'open') return 'Open';
    if (outcome === 'breakeven') return 'Breakeven';
    if (outcome === 'skipped') return 'Skipped';
    return outcome.charAt(0).toUpperCase() + outcome.slice(1);
  };

  const getOutcomePillClass = (outcome) => {
    switch (outcome) {
      case 'win':
        return 'journal-outcome-pill win';
      case 'loss':
        return 'journal-outcome-pill loss';
      case 'breakeven':
        return 'journal-outcome-pill breakeven';
      case 'skipped':
        return 'journal-outcome-pill skipped';
      default:
        return 'journal-outcome-pill open';
    }
  };

  const scrollJournalListIntoView = () => {
  window.requestAnimationFrame(() => {
    journalListTopRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  });
};

  const pageNumbers = useMemo(() => {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }, [totalPages]);

  if (loading) {
    return (
      <div className={`journal-page-shell ${darkMode ? 'dark' : 'light'}`}>
        <div className="journal-editor-card">Loading journal...</div>
      </div>
    );
  }

  return (
    <div className={`journal-page-shell ${darkMode ? 'dark' : 'light'}`}>
      <div className="journal-header">
        <AppBrand theme={theme} />
        <div className="journal-header-copy">
          <h1 className="journal-page-title">Journal Entries</h1>
        </div>

        <div className="journal-header-actions">
          <button
            type="button"
            className="desktop-theme-btn"
            onClick={handleThemeToggle}
            aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {darkMode ? '☀' : '🌙'}
          </button>

          <button
            type="button"
            className="desktop-secondary-btn journal-top-btn"
            onClick={() => navigate('/')}
          >
            Analyzer
          </button>

          <button
            type="button"
            className="desktop-secondary-btn"
            onClick={() => navigate('/performance')}
          >
            Performance
          </button>

          <button
            type="button"
            className="journal-primary-button"
            onClick={handleCreateBlank}
            disabled={saving}
          >
            {saving ? 'Creating...' : 'New Note'}
          </button>
        </div>
      </div>

      {error ? <div className="journal-error-banner">{error}</div> : null}

      <div className="journal-layout journal-layout-accordion">
        <aside className="journal-sidebar">
          <div className="journal-sidebar-card journal-quick-add-card">
            <div className="journal-section-header">
              <h2>Quick Add from Analysis</h2>
            </div>

            {recentAnalyses.length === 0 ? (
              <p className="journal-empty-copy">No analyses available yet.</p>
            ) : (
              <div className="journal-analysis-list">
                {recentAnalyses.map((analysis) => (
                  <button
                    key={analysis.id}
                    type="button"
                    className="journal-analysis-chip"
                    onClick={() => handleCreateFromAnalysis(analysis)}
                    disabled={creatingFromAnalysisId === analysis.id}
                  >
                    {creatingFromAnalysisId === analysis.id
                      ? `Adding ${analysis.ticker}...`
                      : `${analysis.ticker} • ${new Date(
                          analysis.created_at
                        ).toLocaleDateString()}`}
                  </button>
                ))}
              </div>
            )}
          </div>
        </aside>

        <main className="journal-accordion-main">
          <div ref={journalListTopRef} className="journal-list-scroll-anchor" />
          {entries.length === 0 ? (
            <div className="journal-editor-card">
              <h2>No journal entries yet</h2>
              <p className="journal-empty-copy">
                Create a new note or add a snapshot from analysis to get started.
              </p>
            </div>
          ) : (
            <>
              <div className="journal-accordion-list">
                {paginatedEntries.map((entry) => {
                  const isOpen = selectedEntryId === entry.id;
                  const openEntry = isOpen ? selectedEntry : null;

                  return (
                    <div
                      key={entry.id}
                      className={`journal-accordion-card ${isOpen ? 'open' : ''}`}
                    >
                      <button
                        type="button"
                        className="journal-accordion-header"
                        onClick={() => toggleEntryOpen(entry.id)}
                      >
                        <div className="journal-accordion-header-grid">
                          <div className="journal-accordion-cell">
                            <div className="journal-accordion-label">Ticker</div>
                            <div className="journal-accordion-value strong">
                              {entry.ticker || 'Untitled'}
                            </div>
                          </div>

                          <div className="journal-accordion-cell">
                            <div className="journal-accordion-label">Title</div>
                            <div className="journal-accordion-value">
                              {entry.title || 'Untitled'}
                            </div>
                          </div>

                          <div className="journal-accordion-cell">
                            <div className="journal-accordion-label">Outcome</div>
                            <div className="journal-accordion-value">
                              <span className={getOutcomePillClass(entry.outcome_status)}>
                                {formatOutcomeLabel(entry.outcome_status)}
                              </span>
                            </div>
                          </div>

                          <div className="journal-accordion-cell">
                            <div className="journal-accordion-label">Followed Plan</div>
                            <div className="journal-accordion-value">
                              {entry.followed_plan === 'yes'
                                ? 'Yes'
                                : entry.followed_plan === 'no'
                                ? 'No'
                                : 'Not set'}
                            </div>
                          </div>

                          <div className="journal-accordion-cell">
                            <div className="journal-accordion-label">Created</div>
                            <div className="journal-accordion-value">
                              {new Date(entry.created_at).toLocaleDateString()}
                            </div>
                          </div>
                        </div>

                        <div className="journal-accordion-chevron">
                          {isOpen ? '▴' : '▾'}
                        </div>
                      </button>

                      {isOpen && openEntry ? (
                        <div className="journal-accordion-body">
                          <div className="journal-grid">
                            <div className="journal-field full">
                              <label>Ticker</label>
                              <input
                                value={openEntry.ticker || ''}
                                onChange={(e) =>
                                  handleFieldChange('ticker', e.target.value.toUpperCase())
                                }
                                placeholder="NVDA"
                              />
                            </div>

                            <div className="journal-field">
                            <label>Confidence (1-5)</label>
                            <select
                              value={openEntry.confidence || 3}
                              onChange={(e) =>
                                handleFieldChange('confidence', Number(e.target.value))
                              }
                            >
                              <option value={1}>1</option>
                              <option value={2}>2</option>
                              <option value={3}>3</option>
                              <option value={4}>4</option>
                              <option value={5}>5</option>
                            </select>
                          </div>

                          <div className="journal-field">
                            <label>Research Purpose</label>
                            <select
                              value={openEntry.research_intent || ''}
                              onChange={(e) =>
                                handleFieldChange(
                                  'research_intent',
                                  e.target.value || null
                                )
                              }
                            >
                              <option value="">Not set</option>
                              <option value="long_term_investment">
                                Long-term investment
                              </option>
                              <option value="existing_holding">
                                Existing holding
                              </option>
                              <option value="swing_trade">
                                Swing trade
                              </option>
                              <option value="watchlist_research">
                                Watchlist research
                              </option>
                              <option value="exploring">
                                Just exploring
                              </option>
                            </select>
                          </div>
                          <div className="journal-field">
                            <label>Price When Analyzed</label>

                            <input
                              value={
                                Number.isFinite(Number(openEntry.price_at_analysis))
                                  ? new Intl.NumberFormat('en-US', {
                                      style: 'currency',
                                      currency: 'USD',
                                    }).format(Number(openEntry.price_at_analysis))
                                  : 'Not captured'
                              }
                              disabled
                            />
                          </div>
                          <div className="journal-field">
                            <label>Support Zone</label>
                            <input value={openEntry.support_zone || ''} readOnly />
                          </div>

                            <div className="journal-field">
                            <label>Support</label>
                            <input
                              value={openEntry.support_zone || ''}
                              onChange={(e) =>
                                handleFieldChange('support_zone', e.target.value)
                              }
                              placeholder="$100 - $105"
                            />
                          </div>

                          <div className="journal-field">
                            <label>Structural Change Level</label>
                            <input
                              value={openEntry.invalidation_level || ''}
                              onChange={(e) =>
                                handleFieldChange('invalidation_level', e.target.value)
                              }
                              placeholder="$95"
                            />
                          </div>

                          <div className="journal-field">
                            <label>Resistance</label>
                            <input
                              value={openEntry.resistance_level || ''}
                              onChange={(e) =>
                                handleFieldChange('resistance_level', e.target.value)
                              }
                              placeholder="$115 - $120"
                            />
                          </div>

                            <div className="journal-field">
                              <label>Outcome Status</label>
                              <select
                                value={openEntry.outcome_status || 'open'}
                                onChange={(e) =>
                                  handleFieldChange('outcome_status', e.target.value)
                                }
                              >
                                <option value="open">Open</option>
                                <option value="win">Win</option>
                                <option value="loss">Loss</option>
                                <option value="breakeven">Breakeven</option>
                                <option value="skipped">Skipped</option>
                              </select>
                            </div>

                            <div className="journal-field">
                              <label>Followed Plan</label>
                              <select
                                value={openEntry.followed_plan || 'not_set'}
                                onChange={(e) =>
                                  handleFieldChange('followed_plan', e.target.value)
                                }
                              >
                                <option value="not_set">Not set</option>
                                <option value="yes">Yes</option>
                                <option value="no">No</option>
                              </select>
                            </div>

                            <div className="journal-field full">
                              <label>My Notes</label>
                              <textarea
                                value={openEntry.thesis || ''}
                                onChange={(e) =>
                                  handleFieldChange('thesis', e.target.value)
                                }
                                rows={5}
                                placeholder="What are my thoughts on this stock?"
                              />
                            </div>

                            <div className="journal-accordion-actions">
                              <button
                                type="button"
                                className="journal-delete-entry-btn"
                                onClick={() => handleDeleteEntry(entry.id)}
                              >
                                Delete Entry
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>

              {totalPages > 1 ? (
                <div className="journal-pagination">
                  <button
                    type="button"
                    className="journal-pagination-btn"
                    onClick={() => {
                      setCurrentPage((prev) => Math.max(1, prev - 1));
                      setSelectedEntryId(null);
                      scrollJournalListIntoView();
                    }}
                    disabled={currentPage === 1}
                  >
                    Prev
                  </button>

                  <div className="journal-pagination-pages">
                    {pageNumbers.map((pageNumber) => (
                      <button
                        key={pageNumber}
                        type="button"
                        className={`journal-pagination-btn page-number ${
                          currentPage === pageNumber ? 'active' : ''
                        }`}
                        onClick={() => {
                          setCurrentPage(pageNumber);
                          setSelectedEntryId(null);
                          scrollJournalListIntoView();
                        }}
                      >
                        {pageNumber}
                      </button>
                    ))}
                  </div>

                  <button
                    type="button"
                    className="journal-pagination-btn"
                    onClick={() => {
                      setCurrentPage((prev) => Math.min(totalPages, prev + 1));
                      setSelectedEntryId(null);
                      scrollJournalListIntoView();
                    }}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </div>
              ) : null}
            </>
          )}
        </main>
      </div>
    </div>
  );
}

export default JournalPage;